import { uiSurface } from "shared/ui/uiSurface"
import { AttachFile, Close } from "@mui/icons-material"
import {
  Alert,
  Box,
  Button,
  FormControl,
  FormControlLabel,
  IconButton,
  InputAdornment,
  InputLabel,
  MenuItem,
  Radio,
  RadioGroup,
  Select,
  Stack,
  TextField,
  Typography,
} from "@mui/material"
import { DatePicker } from "@mui/x-date-pickers/DatePicker"
import { TimePicker } from "@mui/x-date-pickers/TimePicker"
import dayjs from "dayjs"
import { Dispatch, FC, useCallback, useMemo, useRef } from "react"
import { useTranslation } from "react-i18next"
import { useGetBagTypesQuery } from "../../../apis/appApi"
import { TagSelector } from "./TagSelector"
import type { OrderCreateFormState, RecurrenceType } from "./useOrderCreateForm"

const PAYMENT_METHOD_OPTIONS = [
  "NONE",
  "CASH",
  "CREDIT_CARD",
  "DEBIT",
  "CHEQUE",
]

const DESIRED_ARRIVAL_OPTIONS = [
  "ASAP",
  "URGENT",
  "MORNING",
  "AFTERNOON",
  "EVENING",
  "ANY",
  "BEFORE",
  "AFTER",
  "BETWEEN",
  "OTHER",
]

const RECURRENCE_OPTIONS: RecurrenceType[] = [
  "NONE",
  "DAILY",
  "XDAYS",
  "WEEKLY",
  "WORKDAYS",
  "BIWEEKLY",
  "FOURWEEKS",
  "MONTHLY",
]

type Tag = {
  id: number
  label: string
  color: string
  icon?: string
  shortLabel?: string
  displayOption?: string
}

type Props = {
  state: OrderCreateFormState
  dispatch: Dispatch<any>
  orderTags: Tag[]
}

/**
 * Format a raw cents integer to a dollar string like "1.23".
 */
const formatCents = (cents: number): string => {
  const dollars = Math.floor(cents / 100)
  const remainder = cents % 100
  return `${dollars}.${String(remainder).padStart(2, "0")}`
}

/**
 * Parse display value back to cents integer.
 */
const parseToCents = (display: string): number => {
  const cleaned = display.replace(/[^0-9]/g, "")
  return parseInt(cleaned, 10) || 0
}

/**
 * Build the recurrence info message based on type and delivery date.
 * Replicates legacy AngularJS `updateRecurrenceMessage()` logic.
 */
function getRecurrenceMessage(
  recurrenceType: RecurrenceType,
  deliveryDate: dayjs.Dayjs,
  dailyInterval: number,
  t: (key: string, opts?: Record<string, any>) => string,
): { message: string | null; error: string | null } {
  if (recurrenceType === "NONE") {
    return { message: null, error: null }
  }

  const startDateFormatted = deliveryDate.format("dddd, MMMM D, YYYY")
  const dayOfWeekName = deliveryDate.format("dddd")
  const dayOfMonth = deliveryDate.date()

  switch (recurrenceType) {
    case "DAILY":
      return {
        message: t("orderCreate.recurrence.message.daily", { startDate: startDateFormatted }),
        error: null,
      }
    case "XDAYS":
      return {
        message: t("orderCreate.recurrence.message.xdays", {
          interval: dailyInterval,
          startDate: startDateFormatted,
        }),
        error: null,
      }
    case "WEEKLY":
      return {
        message: t("orderCreate.recurrence.message.weekly", {
          dayOfWeek: dayOfWeekName,
          startDate: startDateFormatted,
        }),
        error: null,
      }
    case "WORKDAYS":
      return {
        message: t("orderCreate.recurrence.message.workdays", { startDate: startDateFormatted }),
        error: null,
      }
    case "BIWEEKLY":
      return {
        message: t("orderCreate.recurrence.message.biweekly", {
          dayOfWeek: dayOfWeekName,
          startDate: startDateFormatted,
        }),
        error: null,
      }
    case "FOURWEEKS":
      return {
        message: t("orderCreate.recurrence.message.fourweeks", {
          dayOfWeek: dayOfWeekName,
          startDate: startDateFormatted,
        }),
        error: null,
      }
    case "MONTHLY":
      if (dayOfMonth > 28) {
        return {
          message: null,
          error: t("orderCreate.recurrence.message.monthOutOfRange"),
        }
      }
      return {
        message: t("orderCreate.recurrence.message.monthly", {
          dayOfMonth,
          startDate: startDateFormatted,
        }),
        error: null,
      }
    default:
      return { message: null, error: null }
  }
}

const MAX_PRESCRIPTION_FILES = 5
const MAX_PRESCRIPTION_FILE_SIZE = 5 * 1024 * 1024 // 5MB

export const OrderSection: FC<Props> = ({ state, dispatch, orderTags }) => {
  const { t } = useTranslation()
  const prescriptionInputRef = useRef<HTMLInputElement>(null)

  const amountCents = parseToCents(state.amount)
  const amountDisplay = state.amount ? formatCents(amountCents) : ""

  const handleAmountKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (
        [
          "Backspace",
          "Delete",
          "Tab",
          "Escape",
          "Enter",
          "ArrowLeft",
          "ArrowRight",
          "Home",
          "End",
        ].includes(e.key)
      ) {
        if (e.key === "Backspace") {
          e.preventDefault()
          const newCents = Math.floor(amountCents / 10)
          dispatch({
            type: "SET_AMOUNT",
            value: newCents > 0 ? formatCents(newCents) : "",
          })
        }
        return
      }

      if (!/^\d$/.test(e.key)) {
        e.preventDefault()
        return
      }

      e.preventDefault()
      const digit = parseInt(e.key, 10)
      const newCents = amountCents * 10 + digit
      if (newCents > 99999999) return
      dispatch({ type: "SET_AMOUNT", value: formatCents(newCents) })
    },
    [amountCents, dispatch],
  )

  const handlePrescriptionFileSelect = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const files = e.target.files
      if (!files) return

      const remaining = MAX_PRESCRIPTION_FILES - state.prescriptionImages.length
      const toProcess = Array.from(files).slice(0, remaining)

      for (const file of toProcess) {
        if (file.size > MAX_PRESCRIPTION_FILE_SIZE) continue
        const reader = new FileReader()
        reader.onload = (evt) => {
          const base64 = (evt.target?.result as string).split(",")[1]
          if (!base64) return
          dispatch({
            type: "ADD_PRESCRIPTION_IMAGE",
            image: {
              data: base64,
              filename: file.name,
              mimeType: file.type,
            },
          })
        }
        reader.readAsDataURL(file)
      }
      e.target.value = ""
    },
    [dispatch, state.prescriptionImages.length],
  )

  // Recurrence message
  const { message: recurrenceMessage, error: recurrenceError } = useMemo(
    () =>
      getRecurrenceMessage(
        state.recurrenceType,
        state.deliveryDate,
        state.dailyRecurrenceInterval,
        t,
      ),
    [state.recurrenceType, state.deliveryDate, state.dailyRecurrenceInterval, t],
  )

  // Whether time pickers are needed
  const showStartTimePicker = ["BEFORE", "AFTER", "BETWEEN"].includes(
    state.desiredArrivalTime,
  )
  const showEndTimePicker = state.desiredArrivalTime === "BETWEEN"

  // Whether amount/callTime should be disabled (legacy: disabled when recurrence active)
  const isRecurrenceActive = state.recurrenceType !== "NONE"

  return (
    <Box {...uiSurface("UI-DLV2-039")}>
      <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 1.5 }}>
        {t("orderCreate.order")}
      </Typography>
      <Stack spacing={2}>
        {/* Workflow radio */}
        <FormControl>
          <Typography variant="caption" color="text.secondary">
            {t("orderCreate.workflow")}
          </Typography>
          <RadioGroup
            row
            value={state.workflow}
            onChange={(e) =>
              dispatch({
                type: "SET_WORKFLOW",
                value: e.target.value as "DELIVERY" | "PICKUP",
              })
            }
          >
            <FormControlLabel
              value="DELIVERY"
              control={<Radio size="small" />}
              label={t("orderCreate.delivery")}
              data-testid="order-create-workflow-delivery"
            />
            <FormControlLabel
              value="PICKUP"
              control={<Radio size="small" />}
              label={t("orderCreate.pickup")}
              data-testid="order-create-workflow-pickup"
            />
          </RadioGroup>
        </FormControl>

        {/* Delivery date */}
        <Box data-testid="order-create-deliveryDate">
          <DatePicker
            label={t("orderCreate.deliveryDate")}
            value={state.deliveryDate}
            onChange={(d) =>
              d && dispatch({ type: "SET_DELIVERY_DATE", value: d })
            }
            slotProps={{
              textField: { size: "small", fullWidth: true },
            }}
          />
        </Box>

        {/* Recurrence type */}
        <FormControl fullWidth size="small">
          <InputLabel>{t("orderCreate.recurrence")}</InputLabel>
          <Select
            data-testid="order-create-recurrence"
            value={state.recurrenceType}
            onChange={(e) =>
              dispatch({
                type: "SET_RECURRENCE_TYPE",
                value: e.target.value as RecurrenceType,
              })
            }
            label={t("orderCreate.recurrence")}
          >
            {RECURRENCE_OPTIONS.map((opt) => (
              <MenuItem key={opt} value={opt}>
                {t(`orderCreate.recurrence.${opt.toLowerCase()}`)}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        {/* XDAYS interval input */}
        {state.recurrenceType === "XDAYS" && (
          <Stack direction="row" spacing={1} alignItems="center">
            <Typography variant="body2" color="text.secondary">
              {t("orderCreate.recurrence.label")}:
            </Typography>
            <TextField
              data-testid="order-create-xdays-interval"
              type="number"
              value={state.dailyRecurrenceInterval}
              onChange={(e) =>
                dispatch({
                  type: "SET_DAILY_RECURRENCE_INTERVAL",
                  value: Math.max(2, parseInt(e.target.value, 10) || 2),
                })
              }
              size="small"
              sx={{ width: 80 }}
              slotProps={{ htmlInput: { min: 2 } }}
            />
            <Typography variant="body2" color="text.secondary">
              {t("orderCreate.recurrence.days")}
            </Typography>
          </Stack>
        )}

        {/* Recurrence info message */}
        {recurrenceMessage && (
          <Alert severity="info" data-testid="order-create-recurrence-message">
            {recurrenceMessage}
          </Alert>
        )}

        {/* Recurrence error message */}
        {recurrenceError && (
          <Alert severity="error" data-testid="order-create-recurrence-error">
            {recurrenceError}
          </Alert>
        )}

        {/* Desired arrival time */}
        <FormControl fullWidth size="small">
          <InputLabel>{t("orderCreate.desiredArrivalTime")}</InputLabel>
          <Select
            data-testid="order-create-arrivalTime"
            value={state.desiredArrivalTime}
            onChange={(e) =>
              dispatch({
                type: "SET_DESIRED_ARRIVAL_TIME",
                value: e.target.value,
              })
            }
            label={t("orderCreate.desiredArrivalTime")}
          >
            {DESIRED_ARRIVAL_OPTIONS.map((opt) => (
              <MenuItem key={opt} value={opt}>
                {t(`orderCreate.arrivalTime.${opt.toLowerCase()}`)}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        {/* Time picker for BEFORE/AFTER/BETWEEN */}
        {showStartTimePicker && (
          <TimePicker
            label={
              state.desiredArrivalTime === "BETWEEN"
                ? t("orderCreate.timeStart")
                : t("orderCreate.timeHour")
            }
            value={
              state.desiredArrivalTimeHour
                ? dayjs(state.desiredArrivalTimeHour, "HH:mm")
                : null
            }
            onChange={(v) =>
              dispatch({
                type: "SET_DESIRED_ARRIVAL_TIME_HOUR",
                value: v ? v.format("HH:mm") : "",
              })
            }
            slotProps={{
              textField: {
                size: "small",
                fullWidth: true,
                "data-testid": "order-create-time-hour",
              } as any,
            }}
          />
        )}

        {showEndTimePicker && (
          <TimePicker
            label={t("orderCreate.timeEnd")}
            value={
              state.desiredArrivalTimeHourEnd
                ? dayjs(state.desiredArrivalTimeHourEnd, "HH:mm")
                : null
            }
            onChange={(v) =>
              dispatch({
                type: "SET_DESIRED_ARRIVAL_TIME_HOUR_END",
                value: v ? v.format("HH:mm") : "",
              })
            }
            slotProps={{
              textField: {
                size: "small",
                fullWidth: true,
                "data-testid": "order-create-time-hour-end",
              } as any,
            }}
          />
        )}

        {/* Amount */}
        <TextField
          data-testid="order-create-amount"
          label={t("orderCreate.amount")}
          value={amountDisplay}
          onKeyDown={handleAmountKeyDown}
          onChange={() => {}}
          size="small"
          fullWidth
          inputMode="numeric"
          disabled={isRecurrenceActive}
          slotProps={{
            input: {
              startAdornment: (
                <InputAdornment position="start">$</InputAdornment>
              ),
            },
          }}
        />

        {/* Payment method */}
        <FormControl fullWidth size="small">
          <InputLabel>{t("orderCreate.paymentMethod")}</InputLabel>
          <Select
            data-testid="order-create-payment-method"
            value={state.paymentMethod}
            onChange={(e) =>
              dispatch({
                type: "SET_PAYMENT_METHOD",
                value: e.target.value,
              })
            }
            label={t("orderCreate.paymentMethod")}
          >
            {PAYMENT_METHOD_OPTIONS.map((opt) => (
              <MenuItem key={opt} value={opt}>
                {t(`orderCreate.paymentMethod.${opt.toLowerCase()}`)}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        {/* External reference */}
        <TextField
          data-testid="order-create-external-reference"
          label={t("orderCreate.externalReference")}
          value={state.externalReference}
          onChange={(e) =>
            dispatch({ type: "SET_EXTERNAL_REFERENCE", value: e.target.value })
          }
          size="small"
          fullWidth
        />

        {/* Order notes */}
        <TextField
          data-testid="order-create-order-notes"
          label={t("orderCreate.orderNotes")}
          value={state.orderNotes}
          onChange={(e) =>
            dispatch({ type: "SET_ORDER_NOTES", value: e.target.value })
          }
          size="small"
          fullWidth
          multiline
          rows={2}
        />

        {/* Employee code */}
        <TextField
          data-testid="order-create-employee-code"
          label={t("orderCreate.employeeCode")}
          value={state.employeeCode}
          onChange={(e) =>
            dispatch({ type: "SET_EMPLOYEE_CODE", value: e.target.value })
          }
          size="small"
          fullWidth
          disabled={isRecurrenceActive}
        />

        {/* Order tags */}
        <TagSelector
          label={t("orderCreate.orderTags")}
          selectedTagIds={state.orderTags}
          availableTags={orderTags}
          onChange={(tagId) => dispatch({ type: "TOGGLE_ORDER_TAG", tagId })}
        />

        {/* Bags */}
        <BagSelector state={state} dispatch={dispatch} />

        {/* Prescription images (F043) */}
        <Box>
          <Typography variant="caption" color="text.secondary" sx={{ mb: 0.5, display: "block" }}>
            {t("orderCreate.prescriptions")}
          </Typography>
          <input
            ref={prescriptionInputRef}
            type="file"
            accept="image/*,.pdf"
            multiple
            style={{ display: "none" }}
            onChange={handlePrescriptionFileSelect}
          />
          <Button
            variant="outlined"
            size="small"
            startIcon={<AttachFile />}
            onClick={() => prescriptionInputRef.current?.click()}
            disabled={state.prescriptionImages.length >= MAX_PRESCRIPTION_FILES}
            data-testid="order-create-add-prescription"
          >
            {t("orderCreate.attachPrescription")}
          </Button>
          {state.prescriptionImages.length > 0 && (
            <Stack direction="row" spacing={1} sx={{ mt: 1, flexWrap: "wrap", gap: 1 }}>
              {state.prescriptionImages.map((img, idx) => (
                <Box
                  key={idx}
                  sx={{
                    position: "relative",
                    width: 80,
                    height: 80,
                    borderRadius: 1,
                    border: "1px solid",
                    borderColor: "grey.300",
                    overflow: "hidden",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    bgcolor: "grey.50",
                  }}
                >
                  {img.mimeType.startsWith("image/") ? (
                    <Box
                      component="img"
                      src={`data:${img.mimeType};base64,${img.data}`}
                      alt={img.filename}
                      sx={{ width: "100%", height: "100%", objectFit: "cover" }}
                    />
                  ) : (
                    <Typography variant="caption" sx={{ px: 0.5, textAlign: "center", wordBreak: "break-all" }}>
                      {img.filename}
                    </Typography>
                  )}
                  <IconButton
                    size="small"
                    onClick={() =>
                      dispatch({ type: "REMOVE_PRESCRIPTION_IMAGE", index: idx })
                    }
                    sx={{
                      position: "absolute",
                      top: -4,
                      right: -4,
                      bgcolor: "background.paper",
                      border: "1px solid",
                      borderColor: "grey.300",
                      width: 20,
                      height: 20,
                      "&:hover": { bgcolor: "error.50" },
                    }}
                  >
                    <Close sx={{ fontSize: 14 }} />
                  </IconButton>
                </Box>
              ))}
            </Stack>
          )}
          <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: "block" }}>
            {t("orderCreate.prescriptionHint", {
              max: MAX_PRESCRIPTION_FILES,
              current: state.prescriptionImages.length,
            })}
          </Typography>
        </Box>
      </Stack>
    </Box>
  )
}

/**
 * BagSelector — renders a row per bag type with a numeric quantity input.
 * Only bag types with quantity > 0 are stored in form state.
 */
const BagSelector: FC<{ state: OrderCreateFormState; dispatch: Dispatch<any> }> = ({
  state,
  dispatch,
}) => {
  const { t } = useTranslation()
  const { data: bagTypes } = useGetBagTypesQuery()

  const handleBagQuantityChange = useCallback(
    (bagTypeId: number, quantity: number) => {
      const clamped = Math.max(0, quantity)
      const existing = state.bags.filter((b) => b.bagTypeId !== bagTypeId)
      const updated = clamped > 0 ? [...existing, { bagTypeId, number: clamped }] : existing
      dispatch({ type: "SET_BAGS", bags: updated })
    },
    [state.bags, dispatch],
  )

  if (!bagTypes || bagTypes.length === 0) return null

  return (
    <Box>
      <Typography variant="caption" color="text.secondary" sx={{ mb: 0.5, display: "block" }}>
        {t("orderCreate.bags")}
      </Typography>
      <Stack spacing={1}>
        {bagTypes.map((bt: any) => {
          const current = state.bags.find((b) => b.bagTypeId === bt.id)
          return (
            <Stack key={bt.id} direction="row" alignItems="center" spacing={1}>
              <Typography variant="body2" sx={{ flex: 1 }}>
                {bt.name}
              </Typography>
              <TextField
                data-testid={`order-create-bag-${bt.id}`}
                type="number"
                value={current?.number ?? 0}
                onChange={(e) =>
                  handleBagQuantityChange(bt.id, parseInt(e.target.value, 10) || 0)
                }
                size="small"
                sx={{ width: 80 }}
                slotProps={{ htmlInput: { min: 0 } }}
              />
            </Stack>
          )
        })}
      </Stack>
    </Box>
  )
}
